// src/hooks/useDebounce.js
import { useCallback, useEffect, useMemo, useRef, useState } from "react";


export default function useDebouncedValue(value, delay = 400) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const id = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(id);
  }, [value, delay]);
  return debounced;
}


export function useDebouncedCallback(fn, delay = 400) {
  const timeoutRef = useRef(null);
  const latestFn = useRef(fn);

  useEffect(() => {
    latestFn.current = fn;
  }, [fn]);

  const cancel = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, []);

  const flush = useCallback(
    (...args) => {
      cancel();
      return latestFn.current(...args);
    },
    [cancel]
  );

  const debounced = useCallback(
    (...args) => {
      cancel();
      timeoutRef.current = setTimeout(() => {
        latestFn.current(...args);
        timeoutRef.current = null;
      }, delay);
    },
    [delay, cancel]
  );

  useEffect(() => cancel, [cancel]); 

  return useMemo(
    () => [debounced, { cancel, flush }],
    [debounced, cancel, flush]
  );
}


export const delay = (ms) => new Promise((res) => setTimeout(res, ms));

export function fakeFetch(payload, ms = 600) {
  return new Promise((resolve) => {
    const dataClone = JSON.parse(JSON.stringify(payload));
    setTimeout(() => resolve({ ok: true, data: dataClone }), ms);
  });
}
